<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useConvexClient } from 'convex-vue';
import { useI18n } from 'vue-i18n';
import { api } from '../../convex/_generated/api';

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  created: [workshopId: string];
}>();

const client = useConvexClient();

const workshopName = ref('');
const workshopSlug = ref('');
const isLoading = ref(false);
const error = ref('');

const isOpen = computed({
  get: () => props.modelValue,
  set: value => {
    if (!value) {
      resetForm();
    }
    emit('update:modelValue', value);
  },
});

const generateSlug = (name: string) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

const updateSlug = () => {
  workshopSlug.value = generateSlug(workshopName.value);
};

const resetForm = () => {
  workshopName.value = '';
  workshopSlug.value = '';
  error.value = '';
  isLoading.value = false;
};

const handleCreate = async () => {
  if (!workshopName.value.trim() || !workshopSlug.value.trim()) {
    error.value = t('workshop.enterNameAndSlug');
    return;
  }

  isLoading.value = true;
  error.value = '';

  try {
    const result = await client.mutation(api.workshops.create, {
      name: workshopName.value.trim(),
      slug: workshopSlug.value.trim(),
    });

    emit('created', result.workshopId);
    isOpen.value = false;
    resetForm();
  } catch (e: any) {
    if (e.message?.includes('slug already exists')) {
      error.value = t('workshop.slugExists', { slug: workshopSlug.value });
    } else {
      error.value = e.message || t('errors.failedToCreate');
    }
    isLoading.value = false;
  }
};

const close = () => {
  isOpen.value = false;
};

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/50" @click="close"></div>

      <!-- Clipboard style modal -->
      <div class="relative clipboard w-full max-w-md">
        <div class="bg-[#f5f5f0] rounded-lg shadow-2xl p-6 pt-8">
          <!-- Paper texture -->
          <div
            class="absolute inset-0 opacity-20 pointer-events-none rounded-lg"
            style="
              background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E&quot;);
            "
          ></div>

          <div class="relative z-10">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-bold text-gray-800">{{ $t('workshop.create') }}</h2>
              <button @click="close" class="text-gray-500 hover:text-gray-700 transition-colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form @submit.prevent="handleCreate" class="space-y-5">
              <div>
                <label class="block text-gray-700 font-medium mb-2">
                  {{ $t('workshop.workshopName') }}
                </label>
                <input
                  v-model="workshopName"
                  @input="updateSlug"
                  type="text"
                  :placeholder="$t('workshop.workshopNamePlaceholder')"
                  class="handwritten-input w-full text-gray-800 bg-white/50 rounded px-3 py-2"
                  required
                  autofocus
                />
              </div>

              <div>
                <label class="block text-gray-700 font-medium mb-2">
                  {{ $t('workshop.slug') }}
                </label>
                <input
                  v-model="workshopSlug"
                  type="text"
                  :placeholder="$t('workshop.slugPlaceholder')"
                  class="handwritten-input w-full text-gray-800 bg-white/50 rounded px-3 py-2"
                  required
                />
                <p class="text-gray-500 text-sm mt-2">
                  {{ $t('workshop.usedInUrl') }} /workshop/{{ workshopSlug || 'my-workshop' }}
                </p>
              </div>

              <div v-if="error" class="p-3 bg-red-100 border border-red-200 rounded-lg">
                <p class="text-red-700 text-sm">{{ error }}</p>
              </div>

              <div class="flex gap-3 pt-2">
                <button
                  type="button"
                  @click="close"
                  class="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  {{ $t('common.cancel') }}
                </button>
                <button
                  type="submit"
                  :disabled="isLoading"
                  class="push-button flex-1 px-4 py-3 rounded-lg font-medium"
                >
                  {{ isLoading ? $t('common.loading') : $t('workshop.create') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
